import { useEffect, useMemo, useRef } from 'react';

import { useAIJobsBatchStatus } from '@/hooks/api/useAIJobsBatchStatus';
import { normalizeAsyncJobStatus } from '@/lib/ai-job-status';
import { resolveGenerationProgressState } from '@/lib/preview-generation';
import { notifyAiGenerationFailureOnce } from '@/lib/ai-generation-failure-toast';
import { useToast } from '@/hooks/use-toast';
import { useGenerationStore } from '@/stores/generationStore';
import { buildImageEditPreviewStartPayload } from '@/lib/edit-image-preview-loading';
import {
  getImageUpscaleTaskScope,
  invalidateImageUpscaleCaches,
  removePendingImageUpscaleJob,
  updateImageUpscaleProgress,
  usePendingImageUpscaleJobs,
} from '@/lib/image-upscale-pending-jobs';

export function ImageUpscaleTaskCoordinator() {
  const pendingJobs = usePendingImageUpscaleJobs();
  const scope = getImageUpscaleTaskScope();
  const { toast } = useToast();
  const settling = useRef(new Set<string>());
  const missingResponses = useRef(new Map<string, { updatedAt: number; count: number }>());
  const jobIds = useMemo(() => pendingJobs.map((item) => item.jobId), [pendingJobs]);
  const { data: statusData, dataUpdatedAt, isError } = useAIJobsBatchStatus(jobIds);

  useEffect(() => {
    if (!statusData || !scope || isError) return;
    for (const pending of pendingJobs) {
      const key = `${scope}:${pending.jobId}`;
      if (settling.current.has(key)) continue;
      const payload = statusData[pending.jobId];
      const store = useGenerationStore.getState();
      let task = store.tasks.find(
        (item) => item.jobId === pending.jobId && item.origin === 'image-upscale'
      );
      if (!payload) {
        const previous = missingResponses.current.get(key);
        if (previous?.updatedAt === dataUpdatedAt) continue;
        const count = (previous?.count ?? 0) + 1;
        missingResponses.current.set(key, { updatedAt: dataUpdatedAt, count });
        if (count >= 3) {
          if (task) store.failTask(task.localTaskId);
          notifyAiGenerationFailureOnce({
            noticeKey: key,
            toast,
            description: '超清任务已不存在或无法访问，请重新确认任务状态',
          });
          removePendingImageUpscaleJob(pending.jobId, scope);
          missingResponses.current.delete(key);
        }
        continue;
      }
      missingResponses.current.delete(key);
      const status = normalizeAsyncJobStatus(payload.status);
      if (!task && (status === 'pending' || status === 'processing')) {
        // 刷新后由同一协调器恢复公共占位，避免普通生成恢复链路重复接管超清。
        const restoredPayload = buildImageEditPreviewStartPayload({
          target: pending.target,
          context: {
            projectId: pending.projectId,
            resourceId: pending.resourceId,
            previewItemId:
              pending.previewItemId ??
              (pending.target.kind === 'project-resource' ? pending.resourceId : undefined),
            aspectRatio: pending.aspectRatio,
          },
        });
        if (restoredPayload) {
          const restored = store.startTask({ ...restoredPayload, origin: 'image-upscale' });
          if (restored) {
            store.bindJobId(restored.taskId, pending.jobId);
            store.releaseTaskSlot(restored.taskId);
            task = useGenerationStore.getState().tasks.find(
              (item) => item.localTaskId === restored.taskId
            );
          }
        }
      }
      const progress = resolveGenerationProgressState(
        payload,
        pending.progressState?.progress ?? 0
      );
      updateImageUpscaleProgress(pending.jobId, progress, scope);
      if (task && task.status === 'processing') {
        store.updateTaskProgress(task.localTaskId, {
          progress: progress.progress,
          phase: progress.phase,
          progressMessage: '正在超清',
          estimatedRemainingSeconds: progress.estimatedRemainingSeconds,
          pollIntervalMs: progress.pollIntervalMs,
        });
      }
      if (status !== 'completed' && status !== 'failed' && status !== 'cancelled') continue;
      settling.current.add(key);
      void (async () => {
        try {
          if (status === 'completed') {
            // 与生成任务相同，保留短暂的完成过渡并等结果刷新后撤下遮罩。
            await Promise.all([
              invalidateImageUpscaleCaches(pending.target, pending),
              new Promise((resolve) => window.setTimeout(resolve, 600)),
            ]);
            if (getImageUpscaleTaskScope() !== scope) return;
            if (task) {
              const output = payload.outputData;
              const url =
                output?.imageUpscaleResult?.url ??
                output?.imageUrl ??
                output?.fileUrl ??
                payload.resultUrl;
              if (typeof url === 'string' && url) {
                store.finishTaskWithResult(task.localTaskId, {
                  outputUrl: url,
                  thumbnailUrl: output?.thumbnailUrl ?? url,
                });
                store.removeCurrentByTarget(task.targetKey);
              } else {
                store.finishTask(task.localTaskId);
              }
            }
          } else {
            if (task) store.failTask(task.localTaskId);
            if (status === 'cancelled') {
              toast({ title: '超清任务已取消' });
            } else {
              notifyAiGenerationFailureOnce({
                noticeKey: key,
                toast,
                description: progress.errorMessage,
                fallbackDescription: '图片超清失败，请重试',
              });
            }
          }
          removePendingImageUpscaleJob(pending.jobId, scope);
        } catch {
          // 刷新结果失败仍需结束等待；保留原图，由用户重新打开列表获取结果。
          if (getImageUpscaleTaskScope() !== scope) return;
          if (task) store.finishTask(task.localTaskId);
          toast({ title: '超清已完成', description: '结果刷新失败，请刷新页面查看' });
          removePendingImageUpscaleJob(pending.jobId, scope);
        } finally {
          settling.current.delete(key);
        }
      })();
    }
  }, [dataUpdatedAt, isError, pendingJobs, scope, statusData, toast]);

  return null;
}
